// Portal one-click-login landing. The portal redirects the browser here with
// a short-lived, single-use `?token=...`. We swap it for a real session, then
// hand off to the authed app. If the token is missing/expired/already-used,
// show a friendly dead-end with a path back to normal sign-in.
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { exchangeSsoToken } from "../api/auth";
import { ApiError } from "../api/client";
import { useAuth } from "../auth/AuthContext";

export function SsoPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();
  const { refresh } = useAuth();

  const [status, setStatus] = useState<"exchanging" | "failed">("exchanging");
  const [error, setError] = useState<string | null>(null);
  // The token is single-use; guard against a double-exchange (React StrictMode
  // re-runs mount effects in dev, which would burn the token on the first call
  // and 400 on the second). One attempt per page load.
  const attempted = useRef(false);

  useEffect(() => {
    if (!token) {
      setError("This sign-in link is missing its token.");
      setStatus("failed");
      return;
    }
    if (attempted.current) return;
    attempted.current = true;
    (async () => {
      try {
        await exchangeSsoToken(token);
        await refresh(); // hydrate AuthContext from the freshly-stored token
        navigate("/edit", { replace: true });
      } catch (err) {
        setError(
          err instanceof ApiError ? err.detail : "This sign-in link could not be used.",
        );
        setStatus("failed");
      }
    })();
    // token is the only input; refresh/navigate are stable enough for a one-shot effect
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <>
      <header className="flex items-center justify-between border-b border-rule-soft px-7 py-5">
        <Link to="/" className="font-display text-2xl tracking-[-0.04em] text-ink">
          Varo
        </Link>
        <Link to="/login" className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-mute hover:text-terra">
          Back to sign in
        </Link>
      </header>

      <main className="grid min-h-[80vh] place-items-center p-7">
        <div className="w-full max-w-xl rounded-lg border border-rule bg-cream-card p-12 text-center shadow-md">
          {status === "exchanging" && (
            <>
              <p className="eyebrow">signing you in…</p>
              <h1 className="mt-4 text-3xl tracking-tight">One moment.</h1>
            </>
          )}

          {status === "failed" && (
            <>
              <p className="eyebrow text-plum">Couldn't sign you in</p>
              <h1 className="mt-4 mb-5 text-3xl tracking-tight">
                This sign-in link has expired or was already used.
              </h1>
              <p className="text-ink-soft">{error}</p>
              <div className="mt-7 flex justify-center gap-3">
                <Link to="/login" className="btn btn-secondary">
                  Go to sign in
                </Link>
              </div>
            </>
          )}
        </div>
      </main>
    </>
  );
}
